import { useCallback, useState } from "react";
import { getRepositoryPipelineRuns } from "../api";
import { useToast } from "./use-toast";

// Helper function to filter pipelines by date
const filterByDate = (pipelines, daysLimit = 30) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysLimit);

  return pipelines.filter((pipeline) => {
    const createdDate = new Date(pipeline.created_on);
    return createdDate >= cutoffDate;
  });
};

export const usePipelineRuns = () => {
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const fetchPipelineRuns = useCallback(async (workspace, repoSlug, repositories) => {
    try {
      setIsLoadingRuns(true);

      // Get repository details
      const repo = repositories.find((r) => r.slug === repoSlug);

      // Only fetch if it's a frontend repository
      const isFrontendRepo =
        repo &&
        (repo.name.toLowerCase().startsWith("frontend") ||
          repo.slug.toLowerCase().startsWith("frontend"));

      if (!isFrontendRepo) {
        console.log(`Skipping non-frontend repository: ${repoSlug}`);
        setPipelineRuns([]);
        setIsLoadingRuns(false);
        return;
      }

      // Fetch the latest pipelines with a single API call without filtering by state
      // We'll filter the results on the client side
      const params = {
        pagelen: 20, // Increased since we're fetching all states
        sort: "-created_on",
      };

      console.log(
        `Fetching latest pipelines for ${workspace}/${repoSlug} with params:`,
        params
      );
      const data = await getRepositoryPipelineRuns(workspace, repoSlug, params);
      console.log(
        `Received ${data.values?.length || 0} total pipelines from API`
      );

      // Log the pipeline counts
      if (data.values && data.values.length > 0) {
        // Log the first pipeline to see its structure
        console.log("Sample pipeline:", data.values[0]);

        // Count how many of each type we have
        const counts = data.values.reduce((acc, pipeline) => {
          const state = pipeline.state?.name || "unknown";
          const result = pipeline.state?.result?.name || "none";
          const status = pipeline.status?.name || "unknown";
          const key = `${state}:${result}:${status}`;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        console.log("Pipeline counts by state/result/status:", counts);

        // Filter to only keep IN_PROGRESS pipelines client-side
        let filteredPipelines = data.values.filter(
          (p) => p.state?.name === "IN_PROGRESS"
        );

        // Further filter to only include pipelines from the last 30 days
        filteredPipelines = filterByDate(filteredPipelines);

        console.log(
          `Filtered to ${filteredPipelines.length} IN_PROGRESS pipelines from the last 30 days out of ${data.values.length} total`
        );

        // Replace the original values with our filtered ones
        data.values = filteredPipelines;
      }

      setPipelineRuns(
        (data.values || []).map((run) => ({
          ...run,
          repository: {
            slug: repoSlug,
            name:
              repositories.find((r) => r.slug === repoSlug)?.name || repoSlug,
          },
        }))
      );
      setIsLoadingRuns(false);
    } catch (err) {
      console.error("Error in fetchPipelineRuns:", err);
      setError("Failed to load pipeline runs");
      setIsLoadingRuns(false);
      toast({
        title: "Error",
        description: "Failed to load pipeline runs",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchPipelinesForAllRepos = useCallback(async (workspace, repositories) => {
    setIsLoadingRuns(true);
    setPipelineRuns([]); // Clear existing runs

    try {
      // Filter repositories by project filter from SettingsPage
      const filteredProjects = JSON.parse(
        localStorage.getItem("filteredProjects") || "[]"
      );
      const projectFilteredRepos = repositories.filter(
        (repo) =>
          !filteredProjects.length ||
          !filteredProjects.includes(repo.project?.key)
      );

      // Filter repositories to only those starting with "frontend"
      const frontendRepos = projectFilteredRepos.filter(
        (repo) =>
          repo.name.toLowerCase().startsWith("frontend") ||
          repo.slug.toLowerCase().startsWith("frontend")
      );

      console.log(
        `Found ${frontendRepos.length} frontend repositories out of ${projectFilteredRepos.length} after project filtering`
      );

      // Process repositories in batches to avoid too many parallel requests
      const BATCH_SIZE = 20; // Smaller batch size for more frequent updates

      // Use all frontend repositories
      const reposToCheck = frontendRepos;
      console.log(`Checking all ${reposToCheck.length} frontend repositories`);

      for (let i = 0; i < reposToCheck.length; i += BATCH_SIZE) {
        const batch = reposToCheck.slice(i, i + BATCH_SIZE);
        console.log(
          `Processing batch ${i / BATCH_SIZE + 1}/${Math.ceil(
            reposToCheck.length / BATCH_SIZE
          )}`
        );

        const promises = batch.map(async (repo) => {
          try {
            // Fetch pipelines with a single API call without filtering by state
            // We'll filter the results on the client side
            const params = {
              pagelen: 20, // Increased since we're fetching all states
              sort: "-created_on",
            };

            console.log(`Fetching pipelines for ${workspace}/${repo.slug}`);
            const data = await getRepositoryPipelineRuns(
              workspace,
              repo.slug,
              params
            );

            console.log(
              `Received ${data.values?.length || 0} total pipelines for ${
                repo.name
              }`
            );

            // Filter to include IN_PROGRESS pipelines client-side
            let relevantPipelines = (data.values || []).filter(
              (pipeline) => pipeline.state?.name === "IN_PROGRESS"
            );

            // Further filter to only include pipelines from the last 30 days
            relevantPipelines = filterByDate(relevantPipelines);

            console.log(
              `Found ${relevantPipelines.length} relevant IN_PROGRESS pipelines from the last 30 days for ${repo.name}`
            );

            // Return the filtered pipelines with repository info
            return relevantPipelines.map((run) => ({
              ...run,
              repository: {
                slug: repo.slug,
                name: repo.name,
              },
            }));
          } catch (error) {
            console.error(`Error fetching pipelines for ${repo.slug}:`, error);
            return [];
          }
        });

        // Process each batch and update the UI immediately
        const batchResults = await Promise.all(promises);
        const newBatchPipelines = batchResults.flat();

        // Update the pipelineRuns state with the accumulated results
        setPipelineRuns((currentRuns) => {
          const allRuns = [...currentRuns, ...newBatchPipelines];
          // Deduplicate by uuid
          const uniqueRunsMap = new Map();
          allRuns.forEach((run) => {
            if (run.uuid) {
              uniqueRunsMap.set(run.uuid, run);
            }
          });
          const uniqueRuns = Array.from(uniqueRunsMap.values());
          uniqueRuns.sort(
            (a, b) => new Date(b.created_on) - new Date(a.created_on)
          );
          return uniqueRuns;
        });
      }

      // Completed loading
      setIsLoadingRuns(false);

      // Log final statistics
      setPipelineRuns((currentRuns) => {
        // Count pipelines by state
        const counts = currentRuns.reduce((acc, pipeline) => {
          const state = pipeline.state?.name || "unknown";
          const result = pipeline.state?.result?.name || "none";
          const status = pipeline.status?.name || "unknown";
          const stage = pipeline.state?.stage?.name || "unknown";
          const key = `${state}:${stage}:${result}:${status}`;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});

        console.log(
          "All pipeline counts by state/stage/result/status:",
          counts
        );
        console.log(
          `Completed loading ${currentRuns.length} total pipelines from all repos (last 30 days only)`
        );

        return currentRuns;
      });
    } catch (err) {
      console.error("Error in fetchPipelinesForAllRepos:", err);
      setError("Failed to load pipelines for all repositories");
      setIsLoadingRuns(false);
      toast({
        title: "Error",
        description: "Failed to load pipelines for all repositories",
        variant: "destructive",
      });
    }
  }, [toast]);

  const clearPipelineRuns = useCallback(() => {
    setPipelineRuns([]);
  }, []);

  return {
    pipelineRuns,
    isLoadingRuns,
    error,
    fetchPipelineRuns,
    fetchPipelinesForAllRepos,
    clearPipelineRuns,
  };
};
